import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../app/hooks';
import { AppDispatch } from '../../app/store';
import { checkCodeScanStatus, checkReportStatus, getLastReportCode, setLoading } from './dashboardSlice';
import { CodeScanStatus, ReportStatus } from './types';

type Timer = ReturnType<typeof setTimeout>;

const STATUS_POLLING_INTERVAL = 5000;

export function useLastReportRedirect(code: string | undefined) {
  const navigate = useNavigate();
  const dispatch: AppDispatch = useAppDispatch();

  useEffect(() => {
    if (!code) {
      dispatch(setLoading(true));
      dispatch(getLastReportCode()).then(({ payload: code }) => {
        navigate(`/report/${code}`);
      });
    }
  }, [code, dispatch, navigate]);
}

export function useReportStatus(code: string | undefined, onReady: () => void) {
  const dispatch: AppDispatch = useAppDispatch();
  const [timer, setTimer] = useState<Timer | null>(null);

  const waitForReport = async (code: string) => {
    const { payload: status } = await dispatch(checkReportStatus(code));

    switch (status) {
      case ReportStatus.Complete:
        onReady();
        return true;
      case ReportStatus.Pending:
        break;
      default:
        return false;
    }

    setTimer(setTimeout(() => waitForReport(code), STATUS_POLLING_INTERVAL));
  };

  useEffect(() => {
    if (code) {
      dispatch(setLoading(true));
      void waitForReport(code);

      return () => clearTimeout(timer as Timer);
    }

    return undefined;
  }, [code, dispatch]);
}

export function useCodeScanStatus(code: string | undefined, onReady: () => void) {
  const dispatch: AppDispatch = useAppDispatch();
  const [timer, setTimer] = useState<Timer | null>(null);

  const waitForCodeScan = async (code: string) => {
    const { payload: status } = await dispatch(checkCodeScanStatus(code));

    switch (status) {
      case CodeScanStatus.Complete:
        onReady();
        return true;
      case CodeScanStatus.Started:
        break;
      default:
        return false;
    }

    setTimer(setTimeout(() => waitForCodeScan(code), STATUS_POLLING_INTERVAL));
  };

  useEffect(() => {
    if (code) {
      void waitForCodeScan(code);

      return () => clearTimeout(timer as Timer);
    }

    return undefined;
  }, [code, dispatch]);
}
